import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { GetstartedService, SignupRequest, SignupResponse, ApiError } from './getstarted.service';
import { ToastrService } from 'ngx-toastr';
import { AuthorizationService } from '../authorization/authorization.service';

import { throwError } from 'rxjs';

interface SignupData {
  lastname: string;
  firstname: string;
  email: string;
  countryCode: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
  typeofStudent: string;
  howDidYouHearAboutUs: string;
}

@Component({
  selector: 'app-getstarted',
  templateUrl: './getstarted.component.html',
  styleUrls: ['./getstarted.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GetstartedComponent implements OnInit {
  step2Loading = false;
  passwordStrengthError = '';
  passwordStrengthMessage = '';
  passwordStrengthValid = false;

  showPassword = false;
  showConfirmPassword = false;

  mobileMenuOpen = false;
  currentStep = 1;
  step2Submitted = false;
  loading = false;
  errorMessage = '';


  submitting = false;


  private signupDta: SignupData = {
    lastname: '',
    firstname: '',
    email: '',
    countryCode: '+233',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    typeofStudent: '',
    howDidYouHearAboutUs: '',
  };

  step1Touched = {
    firstname: false,
    lastname: false,
    email: false,
    phoneNumber: false,
    howDidYouHearAboutUs: false,
  };

  studentTypes = [
    {
      value: 'GHANAIAN',
      label: 'Ghanaian Student',
      flag: '🇬🇭',
      imageUrl: 'https://techbridge.edu.gh/static/g2.webp',
      description: ' Students from Ghana',
    },
    {
      value: 'INTERNATIONAL',
      label: 'International Student',
      flag: '🌍',
      imageUrl: 'https://techbridge.edu.gh/static/f4.webp',
      description: 'Students from other countries',
    },
  ];

  hearAboutUsOptions = [
    { value: 'SOCIAL_MEDIA', label: 'Social Media' },
    { value: 'FRIEND_FAMILY', label: 'Friend or Family' },
    { value: 'SCHOOL_EVENT', label: 'School Event/Fair' },
    { value: 'GOOGLE_SEARCH', label: 'Google Search' },
    { value: 'RADIO_TV', label: 'Radio/TV' },
    { value: 'MARKETING', label: 'Marketing' },
    { value: 'OTHER', label: 'Other' },
  ];

  get signupData(): SignupData {
    return this.signupDta;
  }

  private updateSignupData(partial: Partial<SignupData>): void {
    this.signupDta = { ...this.signupDta, ...partial };
  }

  get hasNumberInPassword(): boolean {
    return /\d/.test(this.signupData.password);
  }

  constructor(
    private getstartedService: GetstartedService,
    private router: Router,
    private toastr: ToastrService,
    private authorizationService: AuthorizationService,
    private cdr: ChangeDetectorRef 
  ) {}

  ngOnInit(): void {}

  trackByValue(index: number, item: { value: string }): string {
    return item.value;
  }

  onEmailInput(event: Event): void {
    const email = (event.target as HTMLInputElement).value.trim();
    this.updateSignupData({ email });

    if (email && this.isEmailValid()) {
      this.storeUserEmail(email);
    }
  }

  private storeUserEmail(email: string): void {
    try {
      localStorage.setItem('user_email', email);
      sessionStorage.setItem('user_email', email);
      this.authorizationService.setUserEmail(email);
    } catch (error) {}
  }

  nextStep(): void {
    if (this.currentStep === 1) {
      if (this.isStep1Valid()) {
        if (this.signupData.email && this.isEmailValid()) {
          this.storeUserEmail(this.signupData.email);
        }
        this.currentStep++;
      } else {
        this.markStep1AsTouched();
        this.showValidationErrors();
      }
    } else if (this.currentStep === 2) {
      this.step2Submitted = true;
      if (this.isStep2Valid()) {
        this.currentStep++;
      } else {
        this.toastr.error('Please select a student type.', 'Validation Error');
      }
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

 // Update the selectStudentType method in your TypeScript file
selectStudentType(type: string): void {
  this.updateSignupData({ typeofStudent: type });
  this.step2Submitted = false;
  this.step2Loading = true; 
  
  // Automatically advance to next step after selection
  setTimeout(() => {
    this.currentStep = 3;
    this.step2Loading = false; 
    this.cdr.detectChanges(); 
  }, 1000);
}

  onPhoneNumberInput(event: Event): void {
    let value = (event.target as HTMLInputElement).value.replace(/\D/g, '');
    value = value.substring(0, 10);
    this.updateSignupData({ phoneNumber: value });
    (event.target as HTMLInputElement).value = value;
  }

  getCompletePhoneNumber(): string {
    return `${this.signupData.countryCode}${this.signupData.phoneNumber}`;
  }


  getPhoneNumberForAPI(): string {
  const countryCodeWithoutPlus = this.signupData.countryCode.replace('+', '');
  const phone = this.signupData.phoneNumber;
  const localNumber = phone.startsWith('0') ? phone.substring(1) : phone; 
  return `${countryCodeWithoutPlus}${localNumber}`;
}

  isEmailValid(): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(this.signupData.email);
  }

isPhoneNumberValid(): boolean {
  const phone = this.signupData.phoneNumber;
  return /^[0-9]{7,15}$/.test(phone); 
}

  isPasswordValid(): boolean {
    const password = this.signupData.password;
    return (
      password.length >= 8 &&
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /[0-9]/.test(password) &&
      /[!@#$%^&*(),.?":{}|<>]/.test(password)
    );
  }

  doPasswordsMatch(): boolean {
    return this.signupData.password === this.signupData.confirmPassword;
  }

  isStep1Valid(): boolean {
    return !!(
      this.signupData.firstname.trim() &&
      this.signupData.lastname.trim() &&
      this.signupData.email.trim() &&
      this.signupData.countryCode &&
      this.signupData.phoneNumber &&
      this.isEmailValid() &&
      this.isPhoneNumberValid() &&
      this.signupData.howDidYouHearAboutUs
    );
  }

  isStep2Valid(): boolean {
    return !!this.signupData.typeofStudent;
  }

  isStep3Valid(): boolean {
    return (
      !!this.signupData.password &&
      !!this.signupData.confirmPassword &&
      this.isPasswordValid() &&
      this.doPasswordsMatch()
    );
  }

 checkPasswordStrength(): void {
  const password = this.signupData.password;
  if (!password) {
    this.passwordStrengthMessage = '';
    this.passwordStrengthValid = false;
    this.passwordStrengthError = '';
    return;
  }

  const checks = {
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSymbol: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    hasMinLength: password.length >= 8,
  };

  const passedChecks = Object.values(checks).filter(Boolean).length;

  this.passwordChecks = {
    upperLower: checks.hasUppercase && checks.hasLowercase,
    symbol: checks.hasSymbol,
    length: checks.hasMinLength,
  };

  if (passedChecks === 5) {
    this.passwordStrengthMessage = 'Strong Password';
    this.passwordStrengthValid = true;
    this.passwordStrengthError = '';
  } else if (passedChecks >= 3) {
    this.passwordStrengthMessage = 'Medium Password';
    this.passwordStrengthValid = false;
    this.passwordStrengthError = '';
  } else {
    this.passwordStrengthMessage = 'Weak Password';
    this.passwordStrengthValid = false;
    this.passwordStrengthError = '';
  }
  
  this.cdr.detectChanges();
}
  passwordChecks = {
    upperLower: false,
    symbol: false,
    length: false,
  };

  getPasswordStrengthLevel(): number {
    const password = this.signupData.password;
    if (!password) return 0;

    const checks = {
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSymbol: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      hasMinLength: password.length >= 8,
    };

    const passedChecks = Object.values(checks).filter(Boolean).length;

    if (passedChecks === 5) return 4;
    if (passedChecks >= 3) return 3;
    if (passedChecks >= 2) return 2;
    if (passedChecks >= 1) return 1;
    return 0;
  }

  isFormValid(): boolean {
    return (
      this.isStep1Valid() &&
      this.isStep2Valid() &&
      this.isStep3Valid() &&
      this.passwordStrengthValid &&
      this.signupData.password === this.signupData.confirmPassword
    );
  }

  togglePasswordVisibility(field: string): void {
    if (field === 'password') {
      this.showPassword = !this.showPassword;
    } else if (field === 'confirmPassword') {
      this.showConfirmPassword = !this.showConfirmPassword;
    }
  }

  markStep1AsTouched(): void {
    this.step1Touched.firstname = true;
    this.step1Touched.lastname = true;
    this.step1Touched.email = true;
    this.step1Touched.phoneNumber = true;
    this.step1Touched.howDidYouHearAboutUs = true;
  }

  showValidationErrors(): void {
    const errors = [];
    if (!this.signupData.firstname.trim()) errors.push('First name is required');
    if (!this.signupData.lastname.trim()) errors.push('Last name is required');
    if (!this.signupData.email.trim()) {
      errors.push('Email is required');
    } else if (!this.isEmailValid()) {
      errors.push('Invalid email address');
    }
  if (!this.signupData.phoneNumber) {
      errors.push('Phone number is required');
    } else if (!this.isPhoneNumberValid()) {
      errors.push('Enter a valid phone number (digits only)');
    }
    if (!this.signupData.howDidYouHearAboutUs) {
      errors.push('Please let us know how you heard about us');
    }

    if (errors.length > 0) {
      this.toastr.error(errors.join('\n'), 'Validation Error');
    }
  }


  onSubmit(): void {
  this.errorMessage = '';

  if (!this.isFormValid()) {
    this.toastr.error('Please complete all required fields correctly.', 'Validation Error');
    return;
  }

  if (!this.doPasswordsMatch()) {
    this.toastr.error('Password and Confirm Password do not match.', 'Validation Error');
    return;
  }

  this.storeUserEmail(this.signupData.email);

  const payload: SignupRequest = {
    firstname: this.signupData.firstname.trim(),
    lastname: this.signupData.lastname.trim(),
    email: this.signupData.email.trim(),
    phoneNumber: this.getPhoneNumberForAPI(),
    typeofStudent: this.signupData.typeofStudent,
    password: this.signupData.password,
    howDidYouHearAboutUs: this.signupData.howDidYouHearAboutUs,
  };

  this.submitting = true;   // ← triggers button loader
  this.loading = true;
  this.cdr.detectChanges();

  this.getstartedService.submitSignup(payload).subscribe(
    (response: SignupResponse & { status?: number }) => {
      this.submitting = false;
      this.loading = false;
      this.cdr.detectChanges();

      if (response && response.status === 200) {
        const signupResponseWithEmail = {
          ...response,
          email: this.signupData.email,
          userEmail: this.signupData.email,
        };
        this.authorizationService.setOtpResponse(signupResponseWithEmail);
        localStorage.setItem('signupResponse', JSON.stringify(signupResponseWithEmail));
        this.toastr.success(response.message || 'Registration successful!', 'Success');
        setTimeout(() => {
          this.router.navigateByUrl('/verify-otp');
        }, 1000);
      } else {
        this.toastr.error(response.message || 'Unexpected response from server.', 'Error');
      }
    },
    (error: ApiError) => {
      this.submitting = false;  // ← reset on error too
      this.loading = false;
      this.cdr.detectChanges();
      this.handleSignupError(error);
    }
  );
}



  private handleSignupError(error: ApiError): void {
    let errorMessage = 'An unexpected error occurred. Please try again.';

    if (error.status === 409) {
      errorMessage = error.error?.message || 'Email is already registered.';
    } else if (error.status === 400) {
      errorMessage = error.error?.message || 'Invalid input. Please check your data.';
    } else if (error.status === 422) {
      errorMessage = error.error?.message || 'Validation failed. Please check your input.';
    } else if (error.status === 500) {
      errorMessage = 'Server error. Please try again later.';
    } else if (error.status === 0) {
      errorMessage = 'Network error. Please check your connection.';
    } else {
      errorMessage = error.error?.message || errorMessage;
    }

    this.toastr.error(errorMessage, 'Registration Error');
    this.errorMessage = errorMessage;
  }

  login(): void {
    this.loading = true;
    setTimeout(() => {
      this.loading = false;
      this.router.navigateByUrl('/login');
    }, 500);
  }

  changePassword(): void {
    this.loading = true;
    setTimeout(() => {
      this.loading = false;
      this.router.navigateByUrl('/reset-password-email');
    }, 500);
  }

  getFieldErrorMessage(field: string): string {
    switch (field) {
      case 'firstname':
        return !this.signupData.firstname.trim() ? 'First name is required' : '';
      case 'lastname':
        return !this.signupData.lastname.trim() ? 'Last name is required' : '';
      case 'email':
        if (!this.signupData.email.trim()) return 'Email is required';
        if (!this.isEmailValid()) return 'Invalid email address';
        return '';
      case 'phoneNumber':
        if (!this.signupData.phoneNumber) return 'Phone number is required';
        if (!this.isPhoneNumberValid()) return 'Invalid 9-digit phone number';
        return '';
      case 'howDidYouHearAboutUs':
        return !this.signupData.howDidYouHearAboutUs ? 'Please let us know how you heard about us' : '';
      case 'password':
        if (!this.signupData.password) return 'Password is required';
        if (!this.isPasswordValid()) return 'Password must be at least 8 characters and strong';
        return '';
      case 'confirmPassword':
        if (!this.signupData.confirmPassword) return 'Confirm password is required';
        if (!this.doPasswordsMatch()) return 'Passwords do not match';
        return '';
      default:
        return '';
    }
  }

  isFieldInvalid(field: string): boolean {
    const touched = this.step1Touched[field as keyof typeof this.step1Touched];
    return touched && !!this.getFieldErrorMessage(field);
  }

  handleError(error: any) {
    console.error('Signup service error:', error);
    return throwError(() => error);
  }
}